import { SeverityLevel } from '@microsoft/applicationinsights-web';

import type { ApiAppliedVouchers, ApiCart } from '../api/cart/responses/cartResponses';
import type { ApiAddMultipleRecipesRequest } from '../api/recipe/requests/recipeApiRequests';
import type { ApiAddMultipleRecipesResponse } from '../api/recipe/responses/recipeApiReponses';
import type { HybrisCart } from '../models/hybris/hybrisCart';
import type { HybrisCartModification } from '../models/hybris/hybrisCartModification';
import type { AppAsyncThunk } from '../store';
import type { StoreContextSettings } from '../store/slices/store/storeContextSettings';

import { appInsights } from '../../../appInsights';
import { normalizeCartToReduxState } from '../../../facades/ecommerce/cart/normalizeCartToReduxState';
import cartItemsSelectors from '../../cart/selectors/cartItemsSelectors';
import { SyncronQueue } from '../api/SyncronQueue';
import { isCartException, isLocalApiBaseException } from '../exceptions/exceptionUtils';
import { currentCartFlow } from '../flow/cart/currentCartFlow';
import { checkoutFlow } from '../flow/checkoutFlow';
import { productFlow } from '../flow/product/productFlow';
import persistance from '../persistance/persistance';
import { cartSelectors } from '../selectors/cartSelectors';
import { storeSelectors } from '../selectors/storeSelectors';
import { userInfoSelectors } from '../selectors/userSelectors';
import { finishLoading, initLoading } from '../store/slices/cart/cartApiStateSlice';
import { cartECouponActions } from '../store/slices/cart/cartECouponSlice';
import { setFullCart } from '../store/slices/cart/cartHydrationActions';
import { cartActions } from '../store/slices/cart/cartItemsSlice';
import { cartRecipeActions } from '../store/slices/cart/cartRecipesSlice';
import { cartSummaryActions } from '../store/slices/cart/cartSummarySlice';
import { errorsActions } from '../store/slices/errors/errorsSlice';
import { getStoreContextFromState } from '../store/slices/store/storeActions.utils';
import { storeActions } from '../store/slices/store/storeSlice';
import { ErrorGroup } from '../store/structureDefinitions/errorsState';
import { DeliveryMode } from '../store/structureDefinitions/storeState';
import { mapPrice } from '../utils/productUtils';
import { resolveUpdatedCartItemQuantity } from './cartThunks.utils';

const cartQueue = new SyncronQueue();
const FETCH_CART_QUEUE_NAME = 'fetchcart';

const buildCartState = async (cart: ApiCart, storeId: string) => {
    const cartProducts = await productFlow.getProductsByIds(
        cart.items.map((cartItem) => cartItem.productId),
        storeId,
    );

    const cartState = normalizeCartToReduxState(cart, cartProducts);
    return cartState;
};

const ensureSyncedCart = (): AppAsyncThunk<Awaited<ApiCart>> => {
    return async (dispatch) => {
        const cartId = currentCartFlow.getCurrentCartId();
        let cart: ApiCart;

        if (!cartId) {
            cart = await dispatch(createCart());
        } else {
            try {
                cart = await currentCartFlow.getCart(cartId);
            } catch (error) {
                if (isCartException(error) && error.code === 'notFound') {
                    cart = await dispatch(createCart());
                } else {
                    throw error;
                }
            }
        }

        await dispatch(ensureStoreContextInSync(cart));
        return cart;
    };
};

const getCartSilent = (): AppAsyncThunk<Awaited<ReturnType<typeof buildCartState>>> => {
    return async (dispatch, getState) => {
        return cartQueue.run(
            async () => {
                const currentStoreId = storeSelectors.currentProductionUnit(getState());
                const cart = await dispatch(ensureSyncedCart());

                const cartState = await buildCartState(cart, currentStoreId);
                dispatch(setFullCart(cartState));

                return cartState;
            },
            {
                uniqueName: FETCH_CART_QUEUE_NAME,
                duplicateUniqueNameOptions: 'enqueueAtTail',
            },
        );
    };
};

const getCart = (): ReturnType<typeof getCartSilent> => {
    return async (dispatch) => {
        dispatch(initLoading());
        try {
            const result = await dispatch(getCartSilent());
            dispatch(finishLoading());
            return result;
        } catch (err) {
            if (isLocalApiBaseException(err)) {
                dispatch(errorsActions.addSingle({ group: ErrorGroup.Cart, error: err }));
            }
            dispatch(finishLoading());
            return Promise.reject(err);
        }
    };
};

const emptyCart = (): AppAsyncThunk => {
    return async (dispatch) => {
        dispatch(initLoading());
        try {
            const cartId = currentCartFlow.getCurrentCartId();
            if (cartId) {
                await currentCartFlow.emptyCart(cartId);
            }
            await dispatch(getCartSilent());
        } catch (err) {
            if (isLocalApiBaseException(err)) {
                dispatch(errorsActions.addSingle({ group: ErrorGroup.Cart, error: err }));
            }
            dispatch(finishLoading());
        }
    };
};

/**
 * Merges two carts
 * @param options.fromCartId from which cart to merge
 * @param options.toCartId destination cart to put items
 * @param options.removeExisting Empties all items in the desination cart before merging, default false
 * @returns Async thunk action
 */
const mergeCart = (options: {
    fromCartId: string;
    toCartId: string;
    removeExisting?: boolean;
}): AppAsyncThunk => {
    return async (dispatch) => {
        dispatch(initLoading());
        try {
            if (options.removeExisting) {
                await currentCartFlow.emptyCart(options.toCartId);
            }

            await currentCartFlow.mergeCart(options.fromCartId, options.toCartId);
            await dispatch(getCartSilent());

            dispatch(finishLoading());
        } catch (err) {
            appInsights.trackException({
                exception: {
                    error: err,
                    name: 'MergeCartErrror',
                    message: `Unable to restore anonymous cart {fromCartId: ${options.fromCartId}, toCartId: ${options.toCartId}`,
                },
                severityLevel: SeverityLevel.Error,
            });
            if (isLocalApiBaseException(err)) {
                dispatch(errorsActions.addSingle({ group: ErrorGroup.Cart, error: err }));
            }
            try {
                await dispatch(getCartSilent());
            } finally {
                dispatch(finishLoading());
            }
        }
    };
};

const removeRecipe = (recipeId: string): AppAsyncThunk => {
    return async (dispatch) => {
        dispatch(initLoading());
        try {
            await cartQueue.run(() => currentCartFlow.removeRecipe(recipeId));
            dispatch(cartRecipeActions.removeRecipe(recipeId));
            await dispatch(getCartSilent());
            dispatch(finishLoading());
        } catch (err) {
            if (isLocalApiBaseException(err)) {
                dispatch(errorsActions.addSingle({ group: ErrorGroup.Cart, error: err }));
            }
            dispatch(finishLoading());
        }
    };
};

const buyMultipleRecipes = (
    request: ApiAddMultipleRecipesRequest,
): AppAsyncThunk<ApiAddMultipleRecipesResponse> => {
    return async (dispatch) => {
        const response = await cartQueue.run(() => currentCartFlow.buyMultipleRecipes(request));
        await dispatch(getCartSilent());
        return response;
    };
};

/** Does not create cart, if we want to use this for anonymous users, that functionality must be added */
const addItems = (
    items: {
        productId: string;
        variantId?: string;
        quantity: number;
    }[],
): AppAsyncThunk => {
    return async (dispatch) => {
        dispatch(initLoading());
        try {
            const cartId = currentCartFlow.getCurrentCartId();
            if (!cartId) {
                throw new Error('No cart id in updateQuantity');
            }
            await currentCartFlow.addItemsBulk(cartId, items);
            await dispatch(getCartSilent());
            dispatch(finishLoading());
        } catch (err) {
            if (isLocalApiBaseException(err)) {
                dispatch(errorsActions.addSingle({ group: ErrorGroup.Cart, error: err }));
            }
            dispatch(finishLoading());
        }
    };
};

const changeRecipePortions = (recipeId: string, portions: number): AppAsyncThunk => {
    return async (dispatch) => {
        dispatch(initLoading());
        try {
            await cartQueue.run(() => currentCartFlow.changeRecipePortions(recipeId, portions));
            dispatch(cartRecipeActions.changeRecipePortions({ recipeId, portions }));
            await dispatch(getCartSilent());
            dispatch(finishLoading());
        } catch (err) {
            if (isLocalApiBaseException(err)) {
                dispatch(errorsActions.addSingle({ group: ErrorGroup.Cart, error: err }));
            }
            dispatch(finishLoading());
        }
    };
};

const changeVariant = (
    cartItemId: string,
    productId: string,
    quantity: number,
    newVariantId: string,
): AppAsyncThunk => {
    return async (dispatch) => {
        dispatch(initLoading());
        try {
            await currentCartFlow.changeItemVariant(cartItemId, productId, quantity, newVariantId);
            await dispatch(getCartSilent());
            dispatch(finishLoading());
        } catch (err) {
            if (isLocalApiBaseException(err)) {
                dispatch(errorsActions.addSingle({ group: ErrorGroup.Cart, error: err }));
            }
            dispatch(finishLoading());
        }
    };
};

const syncStore = async (
    updatedStoreContext: StoreContextSettings,
    dispatch: Parameters<AppAsyncThunk>[0],
) => {
    const validState = getStoreContextFromState(updatedStoreContext);

    persistance.storeContext.set({
        deliveryMode: validState.deliveryMode,
        productionUnitId: validState.productionUnitId,
        pickupPointId: validState.pickupPointId || null,
        storeName: validState.storeName || null,
        zipCode:
            validState.deliveryMode === DeliveryMode.homedelivery
                ? validState.zipCode || undefined
                : undefined,
    });

    await dispatch(storeActions.syncStore(validState));

    appInsights.trackEvent(
        { name: 'ensureStoreContextInSync' },
        {
            deliveryMode: updatedStoreContext.deliveryMode,
            pickupPointId: updatedStoreContext.pickupPointId,
            productionUnitId: updatedStoreContext.productionUnitId,
            storeName: updatedStoreContext.storeName,
        },
    );
};

const ensureStoreContextInSync = (cart: ApiCart): AppAsyncThunk => {
    return async (dispatch) => {
        if (cart.cartData.store && cart.cartData.deliveryMode.mode === DeliveryMode.homedelivery) {
            const newStoreContext = {
                deliveryMode: cart.cartData.deliveryMode.mode,
                productionUnitId: cart.cartData.store.id,
                pickupPointId: undefined,
                storeName: cart.cartData.store.name,
                zipCode: cart.cartData.postCode,
            };

            await syncStore(newStoreContext, dispatch);
            return;
        }

        // if we have pickup store it should always be in pickup mode
        if (cart.cartData.deliveryMode.mode === DeliveryMode.pickup && cart.cartData.pickupStore) {
            const newStoreContext = {
                deliveryMode: cart.cartData.deliveryMode.mode,
                productionUnitId: cart.cartData.pickupStore.productionUnitId,
                pickupPointId: cart.cartData.pickupStore.id,
                storeName: cart.cartData.pickupStore.name,
            };

            /**
             * This is to check if there is a mismatch of delivery mode on cart on in stores, can happen when store is changed and one of delivery modes is removed
             * imo we should definitely not fix backend from frontend, but this is the current behavior
             * */
            const hasValidDeliveryMode = cart.cartData.pickupStore.pickupDeliveryModes.some(
                (pdm) => pdm.code === cart.cartData.deliveryMode.code,
            );

            if (!hasValidDeliveryMode) {
                const firstPickupMode = cart.cartData.pickupStore.pickupDeliveryModes.find(Boolean);

                if (firstPickupMode)
                    await currentCartFlow.setDeliveryMode(
                        cart.cartData.cartId,
                        firstPickupMode.code,
                    );
            }

            await syncStore(newStoreContext, dispatch);
            return;
        }

        /** no delivery mode selected or not enough data to set the correct flow, setup default store,
        take hybris cart store id as the source of truth before our configuration's default store */
        const defaultContext = persistance.storeContext.getDefaultStoreContext();
        const updatedStoreContext = {
            deliveryMode: undefined,
            productionUnitId: cart.cartData.store?.id || defaultContext.productionUnitId,
            pickupPointId: undefined,
            storeName: undefined,
        };

        await syncStore(updatedStoreContext, dispatch);
    };
};

const createCart = (): AppAsyncThunk<ApiCart> => {
    return async (_, getState) => {
        // try resync store context
        const state = getState();
        const isAllowedToSetStoreContext =
            userInfoSelectors.isUserAllowedToDefineStoreContext(state);
        const deliveryMode = storeSelectors.selectedDeliveryModeOrDefault(state);
        const postalCode = cartSelectors.postalCode(state);
        const pickupPointId = storeSelectors.currentPickupPointId(state);

        const cart = isAllowedToSetStoreContext
            ? await currentCartFlow.createCartAsync(deliveryMode, postalCode, pickupPointId)
            : await currentCartFlow.createCartAsync();
        return cart;
    };
};

const updateQuantity = (params: {
    productId: string;
    variantId: string | undefined;
    quantity: number;
    silent: boolean;
}): AppAsyncThunk => {
    return async (dispatch, getState) => {
        const applyServerQuantity = (response: HybrisCart) => {
            const serverQuantity = resolveUpdatedCartItemQuantity({
                productId: params.productId,
                variantId: params.variantId,
                response,
            });
            if (serverQuantity !== undefined && serverQuantity !== params.quantity) {
                dispatch(
                    cartActions.updateCartItem({
                        productId: params.productId,
                        variantId: params.variantId,
                        quantity: serverQuantity,
                    }),
                );
            }
        };

        const updateItem = async (cartId: string) => {
            const response = await currentCartFlow.updateItemQuantity(
                cartId,
                params.productId,
                params.variantId,
                params.quantity,
            );

            dispatch(
                cartSummaryActions.updateTotals({
                    totalPrice: mapPrice(response.totalPrice),
                    totalProductPriceWithDiscounts: mapPrice(
                        response.totalProductPriceWithDiscounts,
                    ),
                }),
            );
            return response;
        };

        if (!params.silent) {
            dispatch(initLoading());
        }
        try {
            // appSelectors can only be used in thunks, not inside "normal"
            await cartQueue.run(async () => {
                const previousCartItemState = cartItemsSelectors.selectById(getState(), {
                    productId: params.productId,
                    variantId: params.variantId,
                });

                const cartId = currentCartFlow.getCurrentCartId();
                if (!cartId) {
                    throw new Error('No cart id in updateQuantity');
                }
                dispatch(
                    cartActions.updateCartItem({
                        productId: params.productId,
                        variantId: params.variantId,
                        quantity: params.quantity,
                    }),
                );

                try {
                    try {
                        applyServerQuantity(await updateItem(cartId));
                    } catch (cartErr) {
                        if (!isCartException(cartErr)) {
                            throw cartErr;
                        }

                        if (cartErr?.code !== 'notFound') {
                            throw cartErr;
                        }
                        const cart = await dispatch(createCart());
                        await dispatch(ensureStoreContextInSync(cart));

                        // try again if cart was not found
                        applyServerQuantity(await updateItem(cart.cartData.cartId!));
                    }
                } catch (err) {
                    dispatch(
                        cartActions.restoreItem({
                            productId: params.productId,
                            variantId: params.variantId,
                            cartItem: previousCartItemState,
                        }),
                    );
                    return Promise.reject(err);
                }
            });

            // Schedule full cart after mutations; if a fetch is already running, queue another at the tail.
            if (!params.silent) {
                await dispatch(getCartSilent());
                dispatch(finishLoading());
            }
        } catch (err) {
            if (isLocalApiBaseException(err)) {
                dispatch(errorsActions.addSingle({ group: ErrorGroup.Cart, error: err }));
            }
            dispatch(finishLoading());
            return Promise.reject(err);
        }
    };
};

const substituteProduct = (
    id: string,
    newProductId: string,
    prevProductId: string,
    storeId: string,
    prevVariantId?: string,
): AppAsyncThunk => {
    return async (dispatch) => {
        try {
            const newCartItem = await cartQueue.run(() =>
                currentCartFlow.substituteProduct(id, newProductId, storeId),
            );
            if (newCartItem) {
                dispatch(
                    cartActions.substituteProduct({
                        newCartItem,
                        prevItemId: id,
                        prevProductId,
                        prevVariantId,
                    }),
                );
                await dispatch(getCart());
            }
        } catch (err) {
            if (isLocalApiBaseException(err)) {
                dispatch(errorsActions.addSingle({ group: ErrorGroup.Cart, error: err }));
            }
            return Promise.reject(err);
        }
    };
};

/** Vouchers and e-coupons are different things in hybris, voucher is for the internal engine that triggers a discount,
 * e-coupons comes from the membership system, read into hybris, then activates a discount if its enabled.
 * Vouchers is a code the user enters */
const applyVoucher = (id: string): AppAsyncThunk<ApiAppliedVouchers | undefined> => {
    return async (dispatch) => {
        try {
            const voucher = await cartQueue.run(() => currentCartFlow.applyVoucher(id));
            await dispatch(getCartSilent());
            return await Promise.resolve(voucher);
        } catch (err) {
            if (isLocalApiBaseException(err)) {
                if (!err.friendlyMessage) {
                    err.friendlyMessage = 'Felaktig Kampanjkod.';
                }
                return Promise.reject(err);
            }
            return Promise.reject(err);
        }
    };
};

const removeVoucher = (id: string): AppAsyncThunk => {
    return async (dispatch) => {
        try {
            await cartQueue.run(() => currentCartFlow.removeVoucher(id));
            await dispatch(getCartSilent());
        } catch (err) {
            if (isLocalApiBaseException(err)) {
                dispatch(errorsActions.addSingle({ group: ErrorGroup.Cart, error: err }));
            }
        }
    };
};

const changeReplaceability = (
    productId: string,
    variantId: string | undefined,
    quantity: number, // needs to be there since we have one endpoint that does not like empty quantity
    newReplaceableValue: boolean,
): AppAsyncThunk<HybrisCartModification> => {
    return async (dispatch, getState) => {
        const previousValue = cartItemsSelectors.selectCartItemReplaceability(getState(), {
            productId,
            variantId,
        });

        try {
            dispatch(
                cartActions.changeCartItemReplaceability({
                    productId,
                    variantId,
                    replaceable: newReplaceableValue,
                }),
            );
            return await cartQueue.run(() =>
                currentCartFlow.changeReplaceability(
                    productId,
                    variantId,
                    quantity,
                    newReplaceableValue,
                ),
            );
        } catch (err) {
            dispatch(
                cartActions.changeCartItemReplaceability({
                    productId,
                    variantId,
                    replaceable: !!previousValue,
                }),
            );
            if (isLocalApiBaseException(err)) {
                dispatch(errorsActions.addSingle({ group: ErrorGroup.Cart, error: err }));
            }
            return Promise.reject(err);
        }
    };
};

const setItemsReplaceability = (replaceable: boolean): AppAsyncThunk => {
    return async (dispatch, getState) => {
        // we block with loader cause backend response with full cart, which can take a couple of seconds...
        dispatch(initLoading());
        const previousValue = cartItemsSelectors.selectAllItemsReplaceability(getState());
        try {
            // optimistically update for perceived speed, rollback in error if it fails
            dispatch(cartActions.setAllCartItemsReplaceability(replaceable));
            await cartQueue.run(() => currentCartFlow.setItemsReplaceability(replaceable));
            dispatch(finishLoading());
        } catch (err) {
            dispatch(cartActions.setAllCartItemsReplaceability(previousValue));
            if (isLocalApiBaseException(err)) {
                dispatch(errorsActions.addSingle({ group: ErrorGroup.Cart, error: err }));
            }
            dispatch(finishLoading());
        }
    };
};

const restoreSavedCart =
    (savedCartId: string): AppAsyncThunk<HybrisCart> =>
    async (dispatch) => {
        const response = await currentCartFlow.restoreSavedCart(savedCartId);
        await dispatch(getCartSilent());
        return response;
    };

const addToCartFromOrder =
    (orderId: string): AppAsyncThunk<HybrisCart> =>
    async (dispatch) => {
        const response = await currentCartFlow.addToCartFromOrder(orderId);
        await dispatch(getCartSilent());
        return response;
    };

const initializeCheckoutFlow = (): AppAsyncThunk => async (dispatch) => {
    await cartQueue.run(() => checkoutFlow.initialize());
    await dispatch(getCartSilent());
};

const changeECouponState = (id: string, newState: boolean): AppAsyncThunk => {
    return async (dispatch, state) => {
        const isAuthenticated = state().user.profile?.isAuthenticated;
        if (!isAuthenticated) {
            return Promise.reject(new Error('User must be authenticated to change e-coupon state'));
        }
        try {
            dispatch(cartECouponActions.setActiveState({ id, active: newState, isDirty: true }));
            if (newState) {
                await cartQueue.run(() => currentCartFlow.activateECoupon(!!isAuthenticated, id));
            } else {
                await cartQueue.run(() => currentCartFlow.deactivateECoupon(!!isAuthenticated, id));
            }
        } catch {
            dispatch(cartECouponActions.setActiveState({ id, active: !newState, isDirty: false }));
        }
        await dispatch(getCartSilent());
    };
};

export const cartThunks = {
    getCartSilent,
    getCart,
    mergeCart,
    emptyCart,
    removeRecipe,
    buyMultipleRecipes,
    addItems,
    changeRecipePortions,
    changeVariant,
    updateQuantity,
    substituteProduct,
    applyVoucher,
    removeVoucher,
    changeReplaceability,
    setItemsReplaceability,
    restoreSavedCart,
    addToCartFromOrder,
    initializeCheckoutFlow,
    changeECouponState,
};
